/**
 * @file CustomerAccountSection.tsx
 * @description Блок «Личный кабинет» в карточке клиента (phase10.6).
 *
 * Показывает статус ЛК клиента (Активирован / Инвайт отправлен / Не приглашён)
 * и даёт кнопку «Отправить инвайт» / «Переотправить». Источник —
 * `GET /admin/customers/:id/account-status`, действие — `POST .../invite`.
 *
 * TD-513: кабинет уже активирован — кнопка неактивна с подсказкой «Забыли
 * пароль»; если сервер всё же ответил 409 `customer_account_already_active`
 * (активировали в другой вкладке) — его текст спокойным уведомлением, без
 * красной ошибки, и статус перечитывается.
 */

import { useEffect, useState } from "react";

import { InviteLinkPanel } from "./InviteLinkPanel";
import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  customerPortal,
  type CustomerAccountStatusDto,
  type InviteResultDto,
} from "../../../../lib/api-customer-portal";

/** Код ответа «кабинет уже активирован» и подсказка по умолчанию (TD-513). */
const ALREADY_ACTIVE_CODE = "customer_account_already_active";
const ALREADY_ACTIVE_HINT = "Кабинет клиента уже активирован — предложите ему «Забыли пароль» на сайте.";

interface Props {
  customerId: string;
  canManage: boolean;
}

export function CustomerAccountSection({ customerId, canManage }: Props) {
  const notify = useNotify();
  const [status, setStatus] = useState<CustomerAccountStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Ссылка последнего инвайта — только в памяти страницы (сервер хранит хэш).
  const [issued, setIssued] = useState<InviteResultDto | null>(null);
  // OQ-70: действующий инвайт сервер отдаёт со ссылкой (токен хранится
  // зашифрованным) — показываем её всегда, а не только сразу после отправки;
  // только что выпущенный инвайт приоритетнее списка (он ещё не перечитан).
  const pendingFromServer = status?.invitations.find((i) => i.status === "pending" && i.inviteUrl);
  const liveInvite = issued?.inviteUrl
    ? { url: issued.inviteUrl, expiresAt: issued.expiresAt }
    : pendingFromServer?.inviteUrl
      ? { url: pendingFromServer.inviteUrl, expiresAt: pendingFromServer.expiresAt }
      : null;

  async function refresh() {
    setLoading(true);
    try {
      setStatus(await customerPortal.getAccountStatus(customerId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function sendInvite() {
    setSending(true);
    try {
      const result = await customerPortal.sendInvite(customerId);
      setIssued(result);
      notify.success("Инвайт отправлен");
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === ALREADY_ACTIVE_CODE) {
        notify.info(err.message || ALREADY_ACTIVE_HINT);
        await refresh();
      } else {
        notify.error(err instanceof ApiError ? err.message : "Не удалось отправить");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="label-editorial mb-3">Личный кабинет</div>
        {loading ? (
          <div className="text-sm text-on-surface-variant">Загружаем…</div>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !status ? (
          <div className="text-sm text-on-surface-variant">Нет данных.</div>
        ) : (
          <AccountSummary status={status} />
        )}

        {canManage && status ? (
          <div className="mt-4 flex flex-col items-start gap-1">
            <Button
              onClick={() => void sendInvite()}
              disabled={sending || status.activated}
              aria-describedby={status.activated ? "account-active-hint" : undefined}
            >
              {sending
                ? "Отправляем…"
                : status.invitedAt && !status.activated
                  ? "Переотправить инвайт"
                  : "Отправить инвайт"}
            </Button>
            {status.activated ? (
              <p id="account-active-hint" className="type-caption text-on-surface-variant">
                {ALREADY_ACTIVE_HINT}
              </p>
            ) : null}
          </div>
        ) : null}

        {liveInvite && status && !status.activated ? (
          <InviteLinkPanel
            url={liveInvite.url}
            expiresAt={liveInvite.expiresAt}
            formatDateTime={formatDateTime}
          />
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

function AccountSummary({ status }: { status: CustomerAccountStatusDto }) {
  const lastInv = status.invitations[0] ?? null;
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {status.activated ? (
          <Badge variant="primary">Активирован</Badge>
        ) : status.invitedAt ? (
          <Badge variant="outline">Инвайт отправлен</Badge>
        ) : (
          <Badge variant="default">Не приглашён</Badge>
        )}
        {status.lastLoginAt ? (
          <span className="text-on-surface-variant">
            последний вход {formatDateTime(status.lastLoginAt)}
          </span>
        ) : null}
      </div>
      {lastInv && !status.activated ? (
        <div className="text-xs text-on-surface-variant">
          Последний инвайт от {formatDateTime(lastInv.createdAt)}, действует до{" "}
          {formatDateTime(lastInv.expiresAt)}.
        </div>
      ) : null}
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
